import React from "react";
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { normalizeProjectCapabilities } from "../../utils/projectCapabilities";
import { normalizeProjectModules } from "../../utils/projectModules";
import { billingAccessLabel, getProjectBillingAccess } from "../../utils/projectBillingAccess";
import MobileProjectSettingsLayout from "./mobile/MobileProjectSettingsLayout";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const SETTINGS_SECTIONS = [
  {
    value: "general",
    label: "Negocio",
    mobileLabel: "Negocio",
    icon: <SettingsRoundedIcon />,
  },
  {
    value: "suscripcion",
    label: "Tu plan",
    mobileLabel: "Plan",
    icon: <WorkspacePremiumRoundedIcon />,
  },
  {
    value: "ecommerce",
    label: "Cobros online",
    mobileLabel: "Cobros",
    icon: <PaymentsRoundedIcon />,
  },
  {
    value: "notificaciones",
    label: "Notificaciones",
    mobileLabel: "Avisos",
    icon: <NotificationsActiveRoundedIcon />,
  },
  {
    value: "accesos",
    label: "Permisos",
    mobileLabel: "Equipo",
    icon: <ManageAccountsRoundedIcon />,
  },
];

function currentSection(pathname) {
  if (pathname.includes("/configuracion/funcionalidades")) return "suscripcion";
  const match = SETTINGS_SECTIONS.find((section) =>
    pathname.includes(`/configuracion/${section.value}`),
  );
  return match?.value || "general";
}

function areaStatus(label, tone = "default") {
  return { label, tone };
}

export default function ProjectSettingsLayout() {
  const { projectId } = useParams();
  const mobile = useMediaQuery("(max-width:899.95px)", { noSsr: true });
  const location = useLocation();
  const navigate = useNavigate();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [readiness, setReadiness] = React.useState(null);

  const project = React.useMemo(() => {
    const matched = projects.find(
      (item) => item.slug === projectId || item.id === projectId || item._id === projectId,
    );
    const activeMatches =
      activeProject?.slug === projectId ||
      activeProject?.id === projectId ||
      activeProject?._id === projectId;
    return matched || (activeMatches ? activeProject : null);
  }, [projects, projectId, activeProject]);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    if (project && project.slug !== activeProject?.slug) setActiveProject(project);
  }, [project, activeProject?.slug, setActiveProject]);

  React.useEffect(() => {
    let cancelled = false;
    const fallback = project?.readiness || null;
    setReadiness(fallback);

    if (!projectId) return () => { cancelled = true; };

    projectsApi
      .getReadiness(projectId)
      .then((data) => {
        if (!cancelled) setReadiness(data?.readiness || fallback);
      })
      .catch(() => {
        if (!cancelled) setReadiness(fallback);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, project?.readiness?.computedAt]);

  const modules = normalizeProjectModules(project?.modules);
  const capabilities = normalizeProjectCapabilities(project?.capabilities);
  const activeModules = Object.values(modules).filter(Boolean).length;
  const activeCapabilities = Object.values(capabilities).filter(Boolean).length;
  const billingAccess = getProjectBillingAccess(project);
  const billingChipColor = billingAccess.isFull
    ? "success"
    : billingAccess.isGrace
      ? "warning"
      : "error";
  const section = currentSection(location.pathname);

  const readinessFacts = readiness?.facts || {};
  const identityReady = readinessFacts.identity
    ? readinessFacts.identity.complete === true
    : Boolean(project?.name && project?.slug);
  const businessDataReady = identityReady && Boolean(project?.businessProfile);
  const domainFact = readinessFacts.customDomain || {};
  const paymentFact = readinessFacts.paymentMethod || {};

  const guidedAreas = React.useMemo(() => {
    const domainStatus = !domainFact.configured
      ? areaStatus("Opcional")
      : domainFact.complete
        ? areaStatus("Listo", "success")
        : areaStatus("Atención", "warning");

    const paymentStatus = !modules.ecommerce
      ? areaStatus("No activo")
      : paymentFact.complete
        ? areaStatus("Listo", "success")
        : areaStatus("Revisar", paymentFact.required ? "warning" : "default");

    return [
      {
        key: "business",
        title: "Datos del negocio",
        description: businessDataReady
          ? "Identidad y tipo de negocio definidos."
          : "Completá la identidad y el tipo de negocio para orientar el panel.",
        status: businessDataReady ? areaStatus("Listo", "success") : areaStatus("Revisar", "warning"),
        icon: <SettingsRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/general?area=business`,
      },
      {
        key: "domain",
        title: "Dominio y publicación",
        description: domainFact.detail || "El dominio propio es opcional; la publicación de WebProfit sigue disponible.",
        status: domainStatus,
        icon: <DnsRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/general?area=domain`,
      },
      {
        key: "payments",
        title: "Cobros online",
        description: modules.ecommerce
          ? (paymentFact.detail || "Revisá la conexión y la moneda de Mercado Pago.")
          : "Activá E-commerce solamente si necesitás cobrar desde la web.",
        status: paymentStatus,
        icon: <PaymentsRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/ecommerce`,
      },
      {
        key: "permissions",
        title: "Permisos y equipo",
        description: "Revisá quién entra al proyecto, sus roles, cajas y permisos.",
        status: areaStatus("Mantenimiento"),
        icon: <ManageAccountsRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/accesos`,
      },
      {
        key: "capabilities",
        title: "Capacidades",
        description: activeCapabilities
          ? `${activeCapabilities} capacidades adaptan el panel a tu operación.`
          : "Elegí solamente las capacidades que realmente usa el negocio.",
        status: activeCapabilities ? areaStatus("Configuradas", "success") : areaStatus("Personalizar"),
        icon: <TuneRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/general?area=capabilities`,
      },
      {
        key: "advanced",
        title: "Configuración avanzada",
        description: "Publicación, historial técnico e identidad visual quedan separados de la operación diaria.",
        status: areaStatus("Opcional"),
        icon: <SettingsSuggestRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/general?area=advanced`,
      },
    ];
  }, [activeCapabilities, businessDataReady, domainFact, modules.ecommerce, paymentFact, projectId]);

  const handleSectionChange = (_event, value) => {
    navigate(`/dashboard/proyectos/${projectId}/configuracion/${value}`);
  };

  const openArea = (area) => navigate(area.path);

  if (mobile) {
    return (
      <MobileProjectSettingsLayout
        sections={SETTINGS_SECTIONS}
        section={section}
        onSectionChange={(value) => handleSectionChange(null, value)}
        project={project}
        activeModules={activeModules}
        billingLabel={billingAccessLabel(billingAccess)}
        billingColor={billingChipColor}
        guidedAreas={guidedAreas}
        onAreaOpen={openArea}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
            Configuración
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
            {project?.name || projectId} · cerrá lo esencial del proyecto y volvé acá para mantenimiento
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            icon={<WorkspacePremiumRoundedIcon />}
            label={`${activeModules} módulos activos`}
            variant="outlined"
            sx={{ fontWeight: 900 }}
          />
          <Chip
            label={billingAccessLabel(billingAccess)}
            color={billingChipColor}
            variant="outlined"
            sx={{ fontWeight: 900 }}
          />
        </Stack>
      </Stack>

      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 950 }}>
                Cierre y mantenimiento del proyecto
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                Revisá primero lo que merece atención. Las tareas operativas siguen en sus secciones habituales del panel.
              </Typography>
            </Box>
            {readiness?.score != null ? (
              <Chip
                icon={<CheckCircleRoundedIcon />}
                label={`${readiness.score}% del inicio listo`}
                color={readiness.score >= 100 ? "success" : "default"}
                variant="outlined"
                sx={{ alignSelf: "flex-start", fontWeight: 900 }}
              />
            ) : null}
          </Stack>

          <Grid container spacing={1.25}>
            {guidedAreas.map((area) => (
              <Grid item xs={12} sm={6} lg={4} key={area.key}>
                <ButtonBase
                  onClick={() => openArea(area)}
                  aria-label={`Abrir ${area.title}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    alignItems: "stretch",
                    textAlign: "left",
                    borderRadius: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      p: 1.55,
                      borderRadius: 3,
                      border: "1px solid rgba(25,36,47,.08)",
                      bgcolor: area.status.tone === "warning" ? "rgba(237,108,2,.035)" : "#fff",
                    }}
                  >
                    <Stack direction="row" spacing={1.15} alignItems="flex-start">
                      <Box sx={{ mt: .15, color: area.status.tone === "warning" ? "warning.main" : BRAND.orange }}>
                        {area.icon}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 950 }}>
                            {area.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={area.status.label}
                            color={area.status.tone === "success" ? "success" : area.status.tone === "warning" ? "warning" : "default"}
                            variant={area.status.tone === "default" ? "outlined" : "filled"}
                            sx={{ height: 22, fontSize: 10.5, fontWeight: 900 }}
                          />
                        </Stack>
                        <Typography variant="caption" sx={{ display: "block", mt: .45, color: "rgba(25,36,47,.58)", lineHeight: 1.35 }}>
                          {area.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </ButtonBase>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card
        elevation={0}
        sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", overflow: "hidden" }}
      >
        <Box sx={{ borderBottom: "1px solid rgba(25,36,47,0.08)", bgcolor: "#fff" }}>
          <Tabs
            value={section}
            onChange={handleSectionChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Secciones de configuración del proyecto"
            sx={{
              px: { xs: 1, md: 2 },
              minHeight: 62,
              "& .MuiTab-root": {
                minHeight: 62,
                textTransform: "none",
                fontWeight: 900,
                px: 2,
              },
              "& .Mui-selected": { color: `${BRAND.orange} !important` },
              "& .MuiTabs-indicator": { bgcolor: BRAND.orange, height: 3 },
            }}
          >
            {SETTINGS_SECTIONS.map((item) => (
              <Tab
                key={item.value}
                value={item.value}
                label={item.label}
                icon={item.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>
        <CardContent sx={{ p: { xs: 2, md: 3.5 }, bgcolor: "#FBFBFD" }}>
          <Outlet />
        </CardContent>
      </Card>
    </Stack>
  );
}
